import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, Target, TrendingUp, MapPin, QrCode, 
  Plus, CheckCircle2, Clock, DollarSign, Users,
  Zap, AlertTriangle, ThumbsUp, ThumbsDown
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Conversions = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentConversions, setRecentConversions] = useState([]);
  const [pendingOptimizations, setPendingOptimizations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Form states
  const [newConversion, setNewConversion] = useState({
    campaign_id: "",
    conversion_type: "visit",
    value: 0,
    notes: ""
  });
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedCampaignForCheckin, setSelectedCampaignForCheckin] = useState("");
  const [checkinCode, setCheckinCode] = useState(null);

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Carregar dados em paralelo
      const [statsRes, conversionsRes, pendingRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/conversions/stats/${clientId}`),
        axios.get(`${API}/conversions/recent/${clientId}`),
        axios.get(`${API}/optimization/pending?client_id=${clientId}`),
        axios.get(`${API}/dashboard/${clientId}`)
      ]);
      
      setStats(statsRes.data);
      setRecentConversions(conversionsRes.data.conversions || []);
      setPendingOptimizations(pendingRes.data.pending_approvals || []);
      setCampaigns(dashboardRes.data.recent_campaigns || []);
      setLocations(dashboardRes.data.locations || []);
      
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const registerConversion = async () => {
    if (!newConversion.campaign_id) {
      toast.error("Selecione uma campanha");
      return;
    }
    
    try {
      await axios.post(`${API}/conversions/manual`, {
        client_id: clientId,
        ...newConversion
      });
      
      toast.success("Conversão registrada!");
      setNewConversion({ campaign_id: "", conversion_type: "visit", value: 0, notes: "" });
      loadData();
    } catch (error) {
      toast.error("Erro ao registrar conversão");
    }
  };

  const generateCheckinCode = async () => {
    if (!selectedLocation || !selectedCampaignForCheckin) {
      toast.error("Selecione local e campanha");
      return;
    }
    
    try {
      const res = await axios.post(
        `${API}/conversions/checkin/generate?location_id=${selectedLocation}&campaign_id=${selectedCampaignForCheckin}`
      );
      setCheckinCode(res.data);
      toast.success("Código de check-in gerado!");
    } catch (error) {
      toast.error("Erro ao gerar código");
    }
  };

  const handleOptimizationApproval = async (notificationId, approved) => {
    try {
      await axios.post(`${API}/optimization/approve`, {
        notification_id: notificationId,
        approved
      });
      
      toast.success(approved ? "Ação aprovada!" : "Ação rejeitada");
      loadData();
    } catch (error) {
      toast.error("Erro ao processar aprovação");
    }
  };

  const runOptimization = async () => {
    try {
      await axios.post(`${API}/optimization/run`);
      toast.success("Otimização iniciada em background");
    } catch (error) {
      toast.error("Erro ao iniciar otimização");
    }
  };

  const conversionTypeLabels = {
    visit: "Visita à loja",
    purchase: "Compra",
    lead: "Lead gerado",
    signup: "Cadastro",
    checkin: "Check-in",
    call: "Ligação",
    message: "Mensagem"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="bg-[#1a1a1a] border-b border-gray-800">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/dashboard/${clientId}`)}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Conversões & Otimização</h1>
                <p className="text-sm text-gray-400">Rastreie resultados e otimize campanhas</p>
              </div>
            </div>
            <Button
              onClick={runOptimization}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Zap className="w-4 h-4 mr-2" />
              Rodar Otimização
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Target className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats?.total_conversions || 0}</p>
                  <p className="text-sm text-gray-400">Conversões Totais</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold">R$ {(stats?.total_value || 0).toFixed(0)}</p>
                  <p className="text-sm text-gray-400">Valor Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{stats?.by_source?.manual?.count || 0}</p>
                  <p className="text-sm text-gray-400">Manuais</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-2xl font-bold">{stats?.by_source?.checkin?.count || 0}</p>
                  <p className="text-sm text-gray-400">Check-ins</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Optimizations Alert */}
        {pendingOptimizations.length > 0 && (
          <Card className="bg-yellow-900/20 border-yellow-600/50 mb-8">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-yellow-400">
                <AlertTriangle className="w-5 h-5" />
                Ações Pendentes de Aprovação ({pendingOptimizations.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingOptimizations.map((opt, idx) => (
                  <div key={idx} className="bg-[#1a1a1a] rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{opt.action?.type}</p>
                      <p className="text-sm text-gray-400">{opt.action?.reason}</p>
                      {opt.action?.recommendation && (
                        <p className="text-sm text-yellow-400 mt-1">{opt.action?.recommendation}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleOptimizationApproval(opt._id, true)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <ThumbsUp className="w-4 h-4 mr-1" />
                        Aprovar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOptimizationApproval(opt._id, false)}
                        className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                      >
                        <ThumbsDown className="w-4 h-4 mr-1" />
                        Rejeitar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="register" className="space-y-6">
          <TabsList className="bg-[#1a1a1a] border border-gray-800">
            <TabsTrigger value="register">Registrar Conversão</TabsTrigger>
            <TabsTrigger value="checkin">Código Check-in</TabsTrigger>
            <TabsTrigger value="history">Histórico</TabsTrigger>
          </TabsList>

          {/* Register Conversion Tab */}
          <TabsContent value="register">
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardHeader>
                <CardTitle>Registrar Nova Conversão</CardTitle>
                <CardDescription>Adicione conversões manualmente</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Campanha</Label>
                    <Select
                      value={newConversion.campaign_id}
                      onValueChange={(v) => setNewConversion({...newConversion, campaign_id: v})}
                    >
                      <SelectTrigger className="bg-[#252525] border-gray-700">
                        <SelectValue placeholder="Selecione uma campanha" />
                      </SelectTrigger>
                      <SelectContent>
                        {campaigns.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.platform} - {c.status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Tipo de Conversão</Label>
                    <Select
                      value={newConversion.conversion_type}
                      onValueChange={(v) => setNewConversion({...newConversion, conversion_type: v})}
                    >
                      <SelectTrigger className="bg-[#252525] border-gray-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(conversionTypeLabels).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Valor (R$)</Label>
                    <Input
                      type="number"
                      value={newConversion.value}
                      onChange={(e) => setNewConversion({...newConversion, value: parseFloat(e.target.value) || 0})}
                      className="bg-[#252525] border-gray-700"
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div>
                    <Label>Observações</Label>
                    <Input
                      value={newConversion.notes}
                      onChange={(e) => setNewConversion({...newConversion, notes: e.target.value})}
                      className="bg-[#252525] border-gray-700"
                      placeholder="Detalhes da conversão..."
                    />
                  </div>
                </div>
                
                <Button onClick={registerConversion} className="w-full bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Registrar Conversão
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Check-in Code Tab */}
          <TabsContent value="checkin">
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Gerar Código de Check-in
                </CardTitle>
                <CardDescription>
                  Crie códigos QR para clientes fazerem check-in nos locais monitorados
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Local</Label>
                    <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                      <SelectTrigger className="bg-[#252525] border-gray-700">
                        <SelectValue placeholder="Selecione um local" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.address?.split(',')[0] || 'Local'} - {loc.city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Campanha</Label>
                    <Select value={selectedCampaignForCheckin} onValueChange={setSelectedCampaignForCheckin}>
                      <SelectTrigger className="bg-[#252525] border-gray-700">
                        <SelectValue placeholder="Selecione uma campanha" />
                      </SelectTrigger>
                      <SelectContent>
                        {campaigns.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.platform} - {c.status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Button onClick={generateCheckinCode} className="w-full bg-blue-600 hover:bg-blue-700">
                  <QrCode className="w-4 h-4 mr-2" />
                  Gerar Código
                </Button>
                
                {checkinCode && (
                  <div className="bg-[#252525] rounded-lg p-6 text-center mt-6">
                    <p className="text-sm text-gray-400 mb-2">Código de Check-in:</p>
                    <p className="text-4xl font-mono font-bold text-green-400 mb-4">
                      {checkinCode.code}
                    </p>
                    <img 
                      src={checkinCode.qr_url} 
                      alt="QR Code" 
                      className="mx-auto mb-4 rounded-lg"
                    />
                    <p className="text-xs text-gray-500">
                      Expira em: {new Date(checkinCode.expires_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardHeader>
                <CardTitle>Conversões Recentes</CardTitle>
              </CardHeader>
              <CardContent>
                {recentConversions.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">Nenhuma conversão registrada ainda</p>
                ) : (
                  <div className="space-y-3">
                    {recentConversions.map((conv, idx) => (
                      <div key={idx} className="bg-[#252525] rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            conv.source === 'manual' ? 'bg-blue-600/20' :
                            conv.source === 'checkin' ? 'bg-green-600/20' :
                            'bg-purple-600/20'
                          }`}>
                            {conv.source === 'manual' && <Users className="w-5 h-5 text-blue-400" />}
                            {conv.source === 'checkin' && <MapPin className="w-5 h-5 text-green-400" />}
                            {conv.source === 'meta_pixel' && <Target className="w-5 h-5 text-purple-400" />}
                          </div>
                          <div>
                            <p className="font-medium">{conv.type_label}</p>
                            <p className="text-sm text-gray-400">
                              {conv.source === 'manual' ? 'Registro Manual' :
                               conv.source === 'checkin' ? 'Check-in' : 'Meta Pixel'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-400">
                            {conv.value > 0 ? `R$ ${conv.value.toFixed(2)}` : '-'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(conv.created_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Conversions;
